"use client";

import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Bookmark,
  Eye,
  EyeOff,
  FileText,
  LoaderCircle,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Trash2,
  Users,
} from "lucide-react";

import { EventPreviewCard } from "@/components/events/event-preview-card";
import { CommentList } from "@/components/feed/comment-list";
import { ContentReportDialog } from "@/components/feed/content-report-dialog";
import { LinkPreviewCard } from "@/components/feed/link-preview-card";
import { PollCard } from "@/components/feed/poll-card";
import { ReactionBar } from "@/components/feed/reaction-bar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MediaMosaic } from "@/components/ui/media-mosaic";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/user-avatar";
import {
  useDeleteOwnPost,
  useToggleSavedPost,
  useUpdateOwnPost,
} from "@/hooks/use-feed";
import { useLinkPreview } from "@/hooks/use-link-preview";
import type { FeedAttachment, FeedPost } from "@/lib/feed/types";
import type { CommunityPostAction } from "@/lib/communities/types";
import {
  cn,
  formatDateTime,
  formatFileSize,
  formatRelativeTime,
  getErrorMessage,
} from "@/lib/utils";

type PostCardProps = {
  post: FeedPost;
  currentUserId: string | null;
  moderation?: {
    pending: boolean;
    onAction: (action: CommunityPostAction) => void;
  };
};

export function PostCard({ post, currentUserId, moderation }: PostCardProps) {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftContent, setDraftContent] = useState(post.content ?? "");
  const [editError, setEditError] = useState<string | null>(null);
  const toggleSaved = useToggleSavedPost(post.id);
  const updatePost = useUpdateOwnPost(post.id);
  const deletePost = useDeleteOwnPost(post.id);
  const editLinkPreview = useLinkPreview(
    draftContent,
    editing,
    post.linkPreview,
  );
  const communityName = post.communities[0]?.name;
  const subtitle = formatRelativeTime(post.publishedAt);
  const isAuthor = Boolean(currentUserId && currentUserId === post.author.id);

  async function saveEdit() {
    const trimmed = draftContent.trim();
    if (!trimmed) {
      setEditError("A bejegyzés szövege nem lehet üres.");
      return;
    }

    setEditError(null);
    try {
      await updatePost.mutateAsync({
        content: trimmed,
        linkPreview: editLinkPreview.preview,
        removeLinkPreview: !editLinkPreview.preview && Boolean(post.linkPreview),
      });
      setEditing(false);
    } catch (error) {
      setEditError(getErrorMessage(error));
    }
  }

  async function removePost() {
    if (!window.confirm("Biztosan törli ezt a bejegyzést?")) {
      return;
    }

    setEditError(null);
    try {
      await deletePost.mutateAsync();
    } catch (error) {
      setEditError(getErrorMessage(error));
    }
  }

  return (
    <article
      className={cn(
        "shadow-surface min-w-0 overflow-x-clip rounded-none border border-x-0 bg-card p-5 md:rounded-2xl md:border-x",
        post.postType === "question" &&
          "border-blue-200/90 ring-1 ring-blue-100/70",
      )}
    >
      <div className="mb-3 flex items-start gap-3">
        <UserAvatar name={post.author.fullName} path={post.author.avatarPath} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{post.author.fullName}</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {isAuthor ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={updatePost.isPending || deletePost.isPending}
                  aria-label="Bejegyzés műveletei"
                  className="text-muted-foreground"
                >
                  {deletePost.isPending ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <MoreHorizontal className="size-4" />
                  )}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={6}
                  className="z-50 min-w-40 rounded-xl border bg-popover p-1 text-popover-foreground shadow-md"
                >
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none hover:bg-muted focus:bg-muted"
                    onSelect={() => {
                      setDraftContent(post.content ?? "");
                      editLinkPreview.reset(post.linkPreview);
                      setEditError(null);
                      setEditing(true);
                    }}
                  >
                    <Pencil className="size-4" />
                    Szerkesztés
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-destructive outline-none hover:bg-destructive/10 focus:bg-destructive/10"
                    onSelect={() => void removePost()}
                  >
                    <Trash2 className="size-4" />
                    Törlés
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : null}
          {moderation ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={moderation.pending}
                aria-label={post.isPinned ? "Rögzítés feloldása" : "Poszt rögzítése"}
                onClick={() =>
                  moderation.onAction(post.isPinned ? "unpin" : "pin")
                }
              >
                {moderation.pending ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : post.isPinned ? (
                  <PinOff className="size-4" />
                ) : (
                  <Pin className="size-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={moderation.pending}
                aria-label={
                  post.isHidden ? "Poszt megjelenítése" : "Poszt elrejtése"
                }
                onClick={() =>
                  moderation.onAction(post.isHidden ? "unhide" : "hide")
                }
              >
                {post.isHidden ? (
                  <Eye className="size-4" />
                ) : (
                  <EyeOff className="size-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={moderation.pending}
                className="text-destructive hover:text-destructive"
                aria-label="Poszt törlése"
                onClick={() => moderation.onAction("delete")}
              >
                <Trash2 className="size-4" />
              </Button>
            </>
          ) : currentUserId && currentUserId !== post.author.id ? (
            <ContentReportDialog targetType="post" targetId={post.id} />
          ) : null}
          {post.postType === "question" ? (
            <Badge className="border-transparent bg-blue-50 font-normal text-blue-700 max-md:hidden">
              Szakmai kérdés
            </Badge>
          ) : null}
          {post.postType === "poll" ? (
            <Badge className="border-transparent bg-sky-50 font-normal text-sky-700">
              Szavazás
            </Badge>
          ) : null}
          {communityName ? (
            <Badge
              variant="secondary"
              className="max-w-40 gap-1 font-normal"
              title={communityName}
            >
              <Users className="size-3 shrink-0" />
              <span className="truncate">{communityName}</span>
            </Badge>
          ) : post.audience === "global" ? (
            <Badge
              variant="secondary"
              className="font-normal max-md:hidden"
            >
              Nyilvános hírfolyam
            </Badge>
          ) : post.audience === "practice" ? (
            <Badge variant="secondary" className="font-normal">
              Praxis hírfolyam
            </Badge>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!currentUserId || toggleSaved.isPending}
            aria-pressed={post.isSaved}
            aria-label={post.isSaved ? "Mentés törlése" : "Bejegyzés mentése"}
            onClick={() => {
              if (!currentUserId) {
                return;
              }

              toggleSaved.mutate({
                userId: currentUserId,
                saved: !post.isSaved,
              });
            }}
            className={cn(
              "text-muted-foreground",
              post.isSaved && "text-primary hover:text-primary",
            )}
          >
            <Bookmark className={cn("size-4", post.isSaved && "fill-current")} />
          </Button>
        </div>
      </div>

      {post.isPinned || post.isHidden ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {post.isPinned ? (
            <Badge variant="secondary" className="gap-1 font-normal">
              <Pin className="size-3" />
              Rögzített bejegyzés
            </Badge>
          ) : null}
          {post.isHidden ? (
            <Badge variant="outline" className="gap-1 font-normal">
              <EyeOff className="size-3" />
              Rejtett bejegyzés
            </Badge>
          ) : null}
        </div>
      ) : null}

      {editing ? (
        <div className="rounded-xl border bg-muted/20 p-3">
          <Textarea
            autoFocus
            value={draftContent}
            maxLength={2000}
            rows={4}
            className="bg-white"
            onChange={(event) => setDraftContent(event.target.value)}
          />
          {editLinkPreview.loading ? (
            <div className="mt-3 overflow-hidden rounded-xl border bg-muted/20">
              <div className="h-20 animate-pulse bg-muted" />
            </div>
          ) : editLinkPreview.preview ? (
            <LinkPreviewCard
              className="mt-3"
              preview={editLinkPreview.preview}
              onRemove={editLinkPreview.dismiss}
            />
          ) : null}
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {2000 - draftContent.length} karakter
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={updatePost.isPending}
                onClick={() => {
                  setEditing(false);
                  setEditError(null);
                }}
              >
                Mégse
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={updatePost.isPending}
                onClick={() => void saveEdit()}
              >
                {updatePost.isPending ? (
                  <LoaderCircle className="size-3.5 animate-spin" />
                ) : null}
                Mentés
              </Button>
            </div>
          </div>
        </div>
      ) : post.content ? (
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
          {post.content}
        </p>
      ) : null}

      {post.editedAt && !editing ? (
        <p className="mt-1.5 text-[10px] text-muted-foreground">
          Módosítva: {formatDateTime(post.editedAt)}
        </p>
      ) : null}

      {editError ? (
        <p className="mt-2 text-xs text-destructive">{editError}</p>
      ) : null}

      {post.poll ? (
        <PollCard
          postId={post.id}
          poll={post.poll}
          enabled={Boolean(currentUserId)}
        />
      ) : null}

      {post.sharedEvent ? (
        <EventPreviewCard event={post.sharedEvent} />
      ) : post.sharedEventId ? (
        <p className="mt-3 rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground">
          Ez az esemény Önnek nem elérhető.
        </p>
      ) : null}

      {post.attachments.length > 0 ? (
        <PostAttachments attachments={post.attachments} />
      ) : null}

      {!editing && post.linkPreview ? (
        <LinkPreviewCard className="mt-3" preview={post.linkPreview} />
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
        <ReactionBar
          postId={post.id}
          counts={post.reactionCounts}
          myReaction={post.myReaction}
          currentUserId={currentUserId}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground"
          onClick={() => setCommentsOpen((open) => !open)}
        >
          <MessageSquare className="size-3.5" />
          {post.commentCount} hozzászólás
        </Button>
      </div>

      {commentsOpen ? (
        <div className="mt-4 border-t pt-4">
          <CommentList
            postId={post.id}
            currentUserId={currentUserId}
            commentsEnabled={post.commentsEnabled}
          />
        </div>
      ) : null}
    </article>
  );
}

function PostAttachments({ attachments }: { attachments: FeedAttachment[] }) {
  const media = attachments.filter(
    (attachment) =>
      (attachment.type === "image" || attachment.type === "video") &&
      attachment.url,
  );
  const documents = attachments.filter(
    (attachment) => !media.some((item) => item.id === attachment.id),
  );

  return (
    <div className="mt-3 space-y-2">
      {media.length > 0 ? (
        <MediaMosaic
          className="border"
          items={media.map((attachment) => ({
            key: attachment.id,
            src: attachment.url!,
            alt: attachment.fileName ?? "Csatolt kép",
            kind: attachment.type === "video" ? "video" : "image",
          }))}
        />
      ) : null}
      {documents.map((attachment) => (
        <a
          key={attachment.id}
          href={attachment.url ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-sm hover:bg-muted/50"
        >
          <FileText className="size-4 text-primary" />
          <span className="min-w-0 flex-1 truncate">
            {attachment.fileName ?? "Dokumentum"}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatFileSize(attachment.sizeBytes)}
          </span>
        </a>
      ))}
    </div>
  );
}
